import { LOCATION_INITIALIZED, registerLocaleData } from "@angular/common";
import { provideHttpClient, withFetch, withInterceptors } from "@angular/common/http";
import { ApplicationConfig, importProvidersFrom, inject, Injector, provideAppInitializer, provideBrowserGlobalErrorListeners } from "@angular/core";
import { provideRouter } from "@angular/router";
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalService,
} from "@azure/msal-angular";
import {
  ILoggerCallback as IMsalLoggerCallback,
  IPublicClientApplication as IMsalPublicClientApplication,
  BrowserCacheLocation as MsalBrowserCacheLocation,
  InteractionType as MsalInteractionType,
  LogLevel as MsalLogLevel,
  PublicClientApplication as MsalPublicClientApplication,
} from "@azure/msal-browser";
import { provideTranslateService, TranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { getIxThemeAsync } from "@siemens/ix-aggrid";
import { IxModule, ToastService } from "@siemens/ix-angular";
import { ModuleRegistry as AgGridModuleRegistry, AllCommunityModule, provideGlobalGridOptions } from "ag-grid-community";
import { pendingRequestsInterceptor$ as pendingRequestsInterceptor } from "ng-http-loader";
import { ModalModule } from "ngx-bootstrap/modal";
import { firstValueFrom } from "rxjs";
import { environment } from "src/environments/environment";
import { provideApiBaseUrl } from "./api/provide-api-base-url";
import { provideNSwagClient } from "./api/provide-n-swag-client";
import { routes } from "./app.routes";
import { provideAppErrorHandler } from "./core/error/app-error-handler";
import { joinPromiseRecord } from "./core/functions/join-promise-record";
import { appHttpInterceptor } from "./core/interceptors/app-http.interceptor";
import { httpErrorInterceptor } from "./core/interceptors/http-error.interceptor";
import { AppThemeService } from "./core/services/app-theme.service";
import { AppToastService } from "./core/services/app-toast.service";

const msalLoggerCallback: IMsalLoggerCallback = (logLevel: MsalLogLevel, message: string) => {
  if (logLevel === MsalLogLevel.Error) {
    console.error(message);
  } else if (logLevel === MsalLogLevel.Warning) {
    console.warn(message);
  } else {
    console.log(message);
  }
};

const msalInstanceFactory = (): IMsalPublicClientApplication =>
  new MsalPublicClientApplication({
    auth: {
      clientId: environment.auth.clientId,
      authority: environment.auth.authority,
      redirectUri: "/",
      postLogoutRedirectUri: null,
    },
    cache: {
      cacheLocation: MsalBrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback: msalLoggerCallback,
        logLevel: MsalLogLevel.Warning,
        piiLoggingEnabled: false,
      },
    },
  });

const msalInterceptorConfigFactory = (): MsalInterceptorConfiguration => ({
  interactionType: MsalInteractionType.Redirect,
  protectedResourceMap: new Map([
    [environment.apiBaseUrl, environment.auth.scopes.trim().split(/\s+/)],
    ["https://graph.microsoft.com/v1.0/me", ["user.read"]],
  ]),
});

const msalGuardConfigFactory = (): MsalGuardConfiguration => ({
  interactionType: MsalInteractionType.Redirect,
  authRequest: {
    scopes: environment.auth.scopes.trim().split(/\s+/),
    prompt: "consent", // Forces user to consent to the scopes on first login
  },
  loginFailedRoute: undefined,
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideHttpClient(withInterceptors([appHttpInterceptor, pendingRequestsInterceptor, httpErrorInterceptor]), withFetch()),
    AppThemeService.getProviderList(),
    importProvidersFrom(IxModule.forRoot(), ModalModule.forRoot()),
    { provide: ToastService, useClass: AppToastService },
    provideTranslateService({
      fallbackLang: "en",
      loader: provideTranslateHttpLoader({ prefix: "assets/i18n/", suffix: ".json" }),
    }),
    // Setup Base Translations
    provideAppInitializer(async (): Promise<void> => {
      const injector = inject(Injector);
      await injector.get(LOCATION_INITIALIZED, Promise.resolve());
      const translate = injector.get(TranslateService);
      translate.addLangs(["en", "de"]);
      const { en, de } = await joinPromiseRecord({
        en: import("public/assets/i18n/en.json").then((m) => m.default),
        de: import("public/assets/i18n/de.json").then((m) => m.default),
      });
      translate.setTranslation("en", en);
      translate.setTranslation("de", de);
      try {
        await firstValueFrom(translate.use(translate.getFallbackLang() ?? "en"));
      } catch (err: unknown) {
        console.error(err);
      }
    }),
    // Setup Locale Data
    provideAppInitializer(async (): Promise<void> => {
      const { de, deExtra } = await joinPromiseRecord({
        de: import("@angular/common/locales/de").then((m) => m.default),
        deExtra: import("@angular/common/locales/extra/de").then((m) => m.default),
      });
      registerLocaleData(de, "de", deExtra);
    }),
    // Setup AgGrid
    provideAppInitializer(async (): Promise<void> => {
      AgGridModuleRegistry.registerModules([AllCommunityModule]);
      provideGlobalGridOptions({
        theme: await getIxThemeAsync(() => import("ag-grid-community")),
      });
    }),
    // Setup Toast Position
    provideAppInitializer((): void => inject(ToastService).setPosition("top-right")),
    { provide: MSAL_INSTANCE, useFactory: msalInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: msalGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: msalInterceptorConfigFactory },
    MsalService,
    MsalBroadcastService,
    provideNSwagClient(),
    provideApiBaseUrl(environment.apiBaseUrl), // For NSwag's Client class
    provideAppErrorHandler(),
  ],
};
